<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import { onDestroy, onMount } from "svelte";
import { renderMarkdown } from "../../utils/markdownParser";

import { fly } from "svelte/transition";
import Alert from "../../components/Alert.svelte";
import DragAndDrop from "../../components/DragAndDrop.svelte";
import FeedbackButtons from "../../components/FeedbackButtons.svelte";
import FileUploadButton from "../../components/FileUploadButton.svelte";
import InputTextarea from "../../components/InputTextarea.svelte";
import SkeletonLoader from "../../components/SkeletonLoader.svelte";
import Warning from "../../components/Warning.svelte";
import sendFeedback from "../../utils/SendFeedback.js";
import uiStore from "../../utils/UIStore";
import websocketsStore from "../../utils/websocketsStore";
import fetchSummary from "./FetchSummary.js";
import fetchFileSummary from "./FetchSummaryFile.js";
import LengthSlider from "./LengthSlider.svelte";

let warningCloud = "";
let warningHalluzination = "";
let summaryText = "";
let sendButtonTooltip = "";
const snapValues = [0, 0.23, 0.43, 0.62, 0.82, 1]; //for length slider
const sliderDefaultValue = 0.23;
let sliderValue = sliderDefaultValue;
let lastMappedSliderValue = mapValue(sliderValue);
let uploadedFile = null;
let uploadedText = null; //from clipboard
let summaryResult = null;
let parsedText = null;
let currentRating = 0;
let llm = null;
let isDropzoneActive = false;
let editText = false;
let resetTextArea = () => {};
let isLoading = false;

onMount(() => {
	document.addEventListener("keydown", handleKeydown);

	//load ui texts from ui Store
	uiStore.textStore.subscribe((store) => {
		if (store.texts != null) {
			let data = store.texts;
			warningCloud = data.warnhinweisCloud;
			summaryText = renderMarkdown(data.summaryText);
			warningHalluzination = renderMarkdown(data.warnhinweisRecherche);
		}
	});

	//load summary state from store
	uiStore.summaryStore.subscribe((store) => {
		summaryResult = store.result;
		currentRating = store.rating;
		uploadedFile = store.file;
		uploadedText = store.text;
	});

	//get llm info from backend
	const url = `${process.env.API_URL}/summary/llms`;
	try {
		const response = fetch(url, {
			method: "GET",
		})
			.then((res) => {
				console.log(res);
				return res.json();
			})
			.then((json) => {
				console.log(json);
				llm = json[0];
			});
	} catch (error) {
		console.log(error);
	}
});

onDestroy(() => {
	document.removeEventListener("keydown", handleKeydown);

	uiStore.summaryStore.update(() => ({
		result: summaryResult,
		rating: currentRating,
		file: uploadedFile,
		text: uploadedText,
	}));
	resetUI();
});

let uiTexts;
uiStore.textStore.subscribe((store) => {
	uiTexts = store.texts;
});

function resetUI() {
	uploadedFile = null;
	uploadedText = null;
	summaryResult = null;
	parsedText = null;
	currentRating = 0;
	editText = false;
	sliderValue = sliderDefaultValue;
	resetTextArea();
	websocketsStore.websocket.resetMessages();
}

async function sendRequest() {
	websocketsStore.websocket.resetMessages();
	summaryResult = null;
	parsedText = null;
	isLoading = true;
	editText = false;
	let mappedSliderValue = mapValue(sliderValue);
	let lastMappedSliderValue = mappedSliderValue;
	let api_result = null;

	try {
		// if file upload
		if (uploadedFile != null) {
			const formData = new FormData();
			formData.append("file", uploadedFile.content);
			formData.append("output_length", mappedSliderValue);
			formData.append("language_model", llm.name);

			api_result = await fetchFileSummary(formData);

			// text from clipboard
		} else if (uploadedText != null) {
			api_result = await fetchSummary(
				uploadedText,
				mappedSliderValue,
				llm.name,
			);
		}

		if (api_result.status !== 200) {
			const res = await api_result.json();
			throw new Error(res.detail);
		}
	} catch (err) {
		isLoading = false;
		websocketsStore.currentMessage.error.update(() => ({
			type: "ERROR",
			message: err.message,
		}));
		return;
	}

	const res = await api_result.json();

	if (res.warning_msg !== "") {
		websocketsStore.currentMessage.warning.update(() => ({
			type: "WARNING",
			message: res.warning_msg,
		}));
	}
	summaryResult = res.summary;
	parsedText = res.parsed_text;

	isLoading = false;
}

function addFiles(fileList) {
	uploadedFile = {
		id: window.crypto.randomUUID(),
		type: "file",
		origin: "upload",
		label: fileList[0].name,
		content: fileList[0],
	};
}

//Paste from clipboard using shortcut
function handleKeydown(event) {
	if (event.ctrlKey && event.key === "v" && uploadedText == null) {
		addTextFromClipboard();
	}
}

function addTextFromClipboard() {
	/*
        Paste text from clipboard
         */
	navigator.clipboard
		.readText()
		.then((text) => {
			if (text.length === 0) {
				websocketsStore.currentMessage.info.update(() => ({
					type: "INFO",
					message: uiTexts.rag.clipboardEmpty,
				}));
				throw Error(uiTexts.rag.clipboardEmpty);
			}
			uploadedText = text;
			return text;
		})
		.catch((reason) => console.debug(`${uiTexts.rag.genericError}·${reason}`));
}

async function giveFeedback(rating, feedbackText) {
	currentRating = rating;

	let type = "text";

	if (uploadedFile != null) {
		console.log(uploadedFile);
		let splittedName = uploadedFile.content.name.split(".");
		type = splittedName[splittedName.length - 1];
	}

	const body = {
		feedback: rating,
		feedback_text: feedbackText,
		llm: {
			name: llm.name,
			label: llm.label,
			is_remote: llm.is_remote,
		},
		source_type: type,
		text_length: parsedText.length,
		desired_summary_length: lastMappedSliderValue * 4000, //number of pages * characters per page
		summary_length: summaryResult.length,
	};

	sendFeedback(body, `${process.env.API_URL}/feedback/summary`);
}

function handleEnterKey(event) {
	if (uploadedText != null) {
		// newline on [SHIFT+Enter]
		if (event.key === "Enter" && event.shiftKey) {
			uploadedText = `${uploadedText}\n`;
			event.preventDefault();
		}
	}
}

//map real slider value to a value that is compatible with the summary backend
function mapValue(x) {
	let mapping = [
		{ x: snapValues[0], y: 0.25 },
		{ x: snapValues[1], y: 0.5 },
		{ x: snapValues[2], y: 1 },
		{ x: snapValues[3], y: 2 },
		{ x: snapValues[4], y: 5 },
		{ x: snapValues[5], y: 10 },
	];

	for (let i = 0; i < mapping.length - 1; i++) {
		const a = mapping[i];
		const b = mapping[i + 1];

		if (x >= a.x && x <= b.x) {
			const t = (x - a.x) / (b.x - a.x); // normalized x
			return a.y + t * (b.y - a.y); // linear interpolation
		}
	}

	if (x <= mapping[0].x) return mapping[0].y;
	if (x >= mapping[mapping.length - 1].x) return mapping[mapping.length - 1].y;
}
</script>

<svelte:head>
  <title>{uiTexts.pageTitles.summary}</title>
</svelte:head>

<main style="padding-top: 90px">
  {#if !llm || llm.is_remote}
    <div class="top-info-bar">
      <Alert>
        <span class="red">{uiTexts.warning.attention}</span>
        <span style="color:#000">{warningCloud} </span>
      </Alert>
    </div>
  {/if}
  <div in:fly={{ y: -20, duration: 500, delay: 200 }} class="page-container">
    <div class="hinweis">
      <div style="flex:1;padding-right:24px">
        <p style="padding-top:0" class="textlink">
          {@html summaryText}
        </p>
      </div>

      <div class="reset">
        <button class="secondary delete" on:click={resetUI}
          >{uiTexts.summaryPage.reset}
        </button>
      </div>
    </div>

    {#if uploadedFile == null && uploadedText == null}
      <div
        class="content-container"
        style="display:flex; flex-direction:row; gap:20px;     align-items: center;"
      >
        <!-- File Upload-->
        <FileUploadButton addFiles={(files) => addFiles(files)} />
        <span>{uiTexts.summaryPage.or}</span>
        <!-- From clipboard-->
        <div class="zwischenablage-input" style="flex:1;max-width:30%">
          <button
            class="secondary"
            on:click={addTextFromClipboard}
            style="width:100%;text-align:left;justify-content:left; height:100%"
          >
            {uiTexts.summaryPage.pasteFromClipboard}
          </button>
        </div>
      </div>
    {/if}

    {#if uploadedFile != null}
      <div class="content-container">
        <div class="source-item" style="width:fit-content; margin:0px;">
          <div class="label">{uploadedFile.label}</div>
          <button
            aria-label={uiTexts.summaryPage.removeFileAria}
            title={uiTexts.summaryPage.removeFileTitle}
            class="delete-source"
            style="background-color:transparent;"
            on:click={() => {
              uploadedFile = null;
            }}
          ></button>
        </div>
      </div>
    {/if}

    {#if uploadedText != null}
      <div class="content-container">
        <div
          class="source-item"
          style="border: {editText
            ? '2px solid black'
            : '1px solid black'}; width:fit-content;margin:0px;"
        >
          <div class="label">{uploadedText.substring(0, 100)}...</div>
          {#if !editText}
            <button
              aria-label={uiTexts.summaryPage.editFileAria}
              title={uiTexts.summaryPage.editFileTitle}
              class="open-source source-button"
              style="background-color:transparent;"
              on:click={() => {
                editText = true;
              }}
            ></button>
          {/if}
          {#if editText}
            <button
              aria-label={uiTexts.summaryPage.closeEditAria}
              title={uiTexts.summaryPage.closeEditTitle}
              class="close-source source-button"
              style="background-color:transparent;"
              on:click={() => {
                editText = false;
              }}
            ></button>
          {/if}
          <div style="border-left: 1px solid black">
            <button
              aria-label={uiTexts.summaryPage.removeTextAria}
              title={uiTexts.summaryPage.removeTextTitle}
              class="delete-source"
              style="background-color:transparent; margin-left:4px;"
              on:click={() => {
                editText = false;
                uploadedText = null;
              }}
            ></button>
          </div>
        </div>
        {#if editText}
          <div style="border: 1px solid black; margin-top:-1px">
            <InputTextarea
              resizable="True"
              bind:newMessage={uploadedText}
              {handleEnterKey}
              bind:reset={resetTextArea}
              name={uiTexts.summaryPage.clipboardInputName}
              placeholder=""
              style="border: 1px solid #000; resize:none;    margin-top: -1px;"
            ></InputTextarea>
          </div>
        {/if}
      </div>
    {/if}

    <div class="content-container send-container">
      <div class="halluzination-warning">
        {@html warningHalluzination}
      </div>

      <div style="display:flex; gap:50px; align-items:center;">
        <LengthSlider bind:sliderValue {snapValues} />

        <button
          class="send-button"
          aria-label={uiTexts.summaryPage.sendRequestAria}
          disabled={(uploadedFile === null && uploadedText === null) ||
            isLoading}
          on:click={() => sendRequest()}
          title={sendButtonTooltip}
        >
        </button>
      </div>
    </div>

    {#if summaryResult != null || isLoading}
      <div style="padding-top: 10px;">
        <!-- Antworten -->
        <div class="frage">
          <span class="flex1"> {uiTexts.summaryPage.summaryHeader} </span>

          <div style="display:flex;">
            {#if llm != null}
              <span style="font-size: small; color: #999999">
                {llm.label}
              </span>
            {/if}

            <button
              title={uiTexts.summaryPage.deleteSummaryTitle}
              class="delete-question source-button"
              aria-label={uiTexts.summaryPage.deleteSummaryAria}
              style="background-color:transparent;"
              on:click={() => (summaryResult = null)}
            ></button>
          </div>
        </div>

        <div class="answers">
          {#if uploadedFile != null}
            <span class="answer-source-type">
              {uiTexts.summaryPage.originalFile} {uploadedFile.label}
            </span>
          {/if}

          {#if uploadedText != null}
            <span class="answer-source-type">
              {uiTexts.summaryPage.originalText} {uploadedText}
            </span>
          {/if}

          {#if isLoading}
            <SkeletonLoader />
          {/if}

          {#if summaryResult != null}
            <div style="padding-top:12px;">
              {@html renderMarkdown(summaryResult)}
            </div>
            <div class="answer-actions">
              <FeedbackButtons
                messageContent={summaryResult}
                buttonClicked={currentRating}
                callback={(rating, feedbackText) =>
                  giveFeedback(rating, feedbackText)}
              />
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>

  <DragAndDrop {isDropzoneActive} addFiles={(files) => addFiles(files)} />
</main>

<style>
  main {
    text-align: center;
    padding-top: 100px;
    padding-bottom: 0px;
    max-width: none;
    height: 100vh;
    justify-content: center;
  }

  .content-container {
    display: flex;
    flex-direction: column;
    background-color: var(--bg-color);
    padding: 10px;
    text-align: left;
  }
  .send-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
</style>
